import React from "react";
import { Form, Formik, FormikHelpers } from "formik";
import { DrawerFormValues } from "src/models/Drawer.model";
import {
  useDrawerInOutMutation,
  useGetAllDrawerDataQuery,
} from "src/services/DrawerService";
import { showToast } from "src/utils/showToaster";
import { useNavigate } from "react-router-dom";
import SideNavLayout from "src/components/layouts/SideNavLayout/SideNavLayout";
import DrawerInOutFlowForm from "../Layouts/DrawerInOutForm";
import { array, number, object, string } from "yup";

const DrawerOutFlowFormWrapper = () => {
  const { data } = useGetAllDrawerDataQuery("");
  const [submitDrawerData] = useDrawerInOutMutation();
  const navigate = useNavigate();

  // Extract physical note types only
  const physicalModes =
    data?.data?.filter((mode: any) => mode?.type === "PHYSICAL") || [];

  // Map API data into form values format
  const initialValues: DrawerFormValues = {
    type: "OUTFLOW",
    reason: "",
    notes: physicalModes.map((mode: any) => ({
      name: mode.name,
      value: mode.value,
      quantity: "", // for user input
      type: mode.type,
      availableQuantity: mode.quantity, // API "quantity" becomes availableQuantity
    })),
  };

  const validationSchema = object().shape({
    reason: string().required("Reason is required"),

    notes: array()
      .of(
        object().shape({
          name: string().required(),
          value: number().required().positive(),
          quantity: string().when("availableQuantity", {
            is: (val: number) => val > 0,
            then: (schema) =>
              schema.test(
                "not-exceed",
                "Cannot exceed available quantity",
                function (value) {
                  const available = this.parent.availableQuantity || 0;
                  return Number(value || 0) <= available;
                }
              ),
            otherwise: (schema) => schema.notRequired(),
          }),
          type: string().required().oneOf(["PHYSICAL"]),
        })
      )
      .min(1, "At least one outflow item is required"),
  });

  const handleSubmit = async (
    values: DrawerFormValues,
    { setSubmitting, resetForm }: FormikHelpers<DrawerFormValues>
  ) => {
    const payload = {
      type: values.type,
      reason: values.reason,
      notes: values.notes
        .filter((note) => Number(note.quantity) > 0)
        .map((note) => ({
          name: note.name,
          value: note.value,
          quantity: Number(note.quantity),
          type: note.type,
        })),
    };

    try {
      const res = await submitDrawerData(payload).unwrap();
      if (res?.status) {
        showToast("success", res?.message);
        navigate("/drawer");
        resetForm();
      } else {
        showToast("error", res?.message || "Submission failed");
      }
    } catch (err: any) {
      showToast("error", err?.data?.message || "API Error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      enableReinitialize
    >
      {(formikProps) => (
        <Form>
          <SideNavLayout>
            {" "}
            <DrawerInOutFlowForm
              formikProps={formikProps}
              isOut={true}
              title="Cash out"
            />
          </SideNavLayout>
        </Form>
      )}
    </Formik>
  );
};

export default DrawerOutFlowFormWrapper;
